"use client";
// 26차 후속 — 운영대시보드 위젯 1차: 금주 등록 신상품.
// products.created_at 이번 주(KST 월~일) 기준. inventory_by_location 합산 총재고 표시.

import { useEffect, useState } from "react";
import Link from "next/link";
import { getRecentProducts, type RecentProductsResponse } from "@/lib/api";

function fmtDate(s: string | null): string {
  if (!s) return "-";
  try {
    const d = new Date(s);
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
  } catch {
    return "-";
  }
}

export default function RecentProductsCard() {
  const [data, setData] = useState<RecentProductsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRecentProducts(50)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "조회 실패");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-gray-700">🆕 금주 등록 신상품</p>
        {data && (
          <span className="text-[11px] text-gray-500">
            {data.week_start} ~ {data.week_end} · {data.total}건
          </span>
        )}
      </div>

      {loading && <p className="text-xs text-gray-400">불러오는 중...</p>}
      {error && <p className="text-xs text-red-600">⚠️ {error}</p>}

      {!loading && !error && data && data.items.length === 0 && (
        <p className="text-xs text-gray-400">이번 주 등록된 신상품이 없습니다.</p>
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <div className="overflow-y-auto max-h-72">
          <table className="w-full text-xs">
            <thead className="text-gray-500 text-[11px] border-b border-gray-100">
              <tr>
                <th className="text-left py-1 pr-2 font-semibold">SKU</th>
                <th className="text-left py-1 pr-2 font-semibold">상품명</th>
                <th className="text-left py-1 pr-2 font-semibold">브랜드</th>
                <th className="text-left py-1 pr-2 font-semibold">카테고리</th>
                <th className="text-right py-1 pr-2 font-semibold">재고</th>
                <th className="text-right py-1 font-semibold">등록</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr
                  key={p.sku}
                  data-testid="recent-product-row"
                  className="border-b border-gray-50 hover:bg-gray-50"
                >
                  <td className="py-1 pr-2 font-mono text-[11px] text-gray-700">
                    <Link href={`/products/${encodeURIComponent(p.sku)}`} className="hover:underline">
                      {p.sku}
                    </Link>
                  </td>
                  <td className="py-1 pr-2 truncate max-w-[180px]" title={p.product_name}>
                    {p.product_name}
                    {!p.is_active && (
                      <span className="ml-1 text-[10px] text-gray-400">(비활성)</span>
                    )}
                  </td>
                  <td className="py-1 pr-2 text-gray-600">{p.brand || "-"}</td>
                  <td className="py-1 pr-2 text-gray-600">{p.category || "-"}</td>
                  <td className="py-1 pr-2 text-right font-semibold text-gray-700">
                    {p.total_stock.toLocaleString("ko-KR")}
                    {p.naver_url ? (
                      <a
                        href={p.naver_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="네이버 스마트스토어"
                        className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                      >
                        N↗
                      </a>
                    ) : null}
                  </td>
                  <td className="py-1 text-right text-gray-500">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
